import { entryIdentityKey, type SavedCitation } from "@/lib/bibliography";
import type { EditableEntry } from "@/lib/citation";

export type MetadataWarningField =
  | "accessed"
  | "author"
  | "container"
  | "date"
  | "editor"
  | "event"
  | "publisher"
  | "title"
  | "url";

export interface ImportDuplicateCandidate {
  kind: "existing" | "batch";
  title?: string;
}

export interface StagedImportEntry {
  duplicate?: ImportDuplicateCandidate;
  entry: EditableEntry;
  id: string;
  included: boolean;
  warnings: MetadataWarningField[];
}

export interface ImportOutcome {
  added: number;
  duplicates: number;
  excluded: number;
  failed: number;
  overLimit: number;
}

const PERIODICAL_TYPES = new Set([
  "article",
  "article-journal",
  "article-magazine",
  "article-newspaper",
]);
const WEB_TYPES = new Set(["post", "post-weblog", "webpage", "web"]);
const EVENT_TYPES = new Set(["paper-conference", "speech"]);

function hasValue(value: string | undefined): boolean {
  return Boolean(value?.trim());
}

function requireField(
  warnings: MetadataWarningField[],
  field: MetadataWarningField,
  value: string | undefined
): void {
  if (!hasValue(value)) {
    warnings.push(field);
  }
}

/**
 * Return source-type-aware metadata prompts. These are advisory review cues, not a
 * completeness score or a formatting gate: CSL can legitimately render sparse records.
 */
export function metadataWarnings(entry: EditableEntry): MetadataWarningField[] {
  const warnings: MetadataWarningField[] = [];
  const type = entry.type?.trim().toLowerCase() ?? "";

  requireField(warnings, "title", entry.title);

  if (type === "book") {
    if (!(hasValue(entry.author) || hasValue(entry.editor))) {
      warnings.push("author", "editor");
    }
    requireField(warnings, "date", entry.issued);
    requireField(warnings, "publisher", entry.publisher);
    return warnings;
  }

  if (type === "chapter") {
    requireField(warnings, "author", entry.author);
    requireField(warnings, "container", entry.containerTitle);
    requireField(warnings, "date", entry.issued);
    requireField(warnings, "publisher", entry.publisher);
    return warnings;
  }

  if (PERIODICAL_TYPES.has(type)) {
    requireField(warnings, "author", entry.author);
    requireField(warnings, "container", entry.containerTitle);
    requireField(warnings, "date", entry.issued);
    return warnings;
  }

  if (WEB_TYPES.has(type)) {
    requireField(warnings, "url", entry.URL);
    requireField(warnings, "date", entry.issued);
    requireField(warnings, "accessed", entry.accessed);
    return warnings;
  }

  if (EVENT_TYPES.has(type)) {
    requireField(warnings, "author", entry.author);
    requireField(warnings, "date", entry.issued);
    requireField(warnings, "event", entry.eventPlace);
    return warnings;
  }

  if (type === "thesis" || type === "report") {
    requireField(warnings, "author", entry.author);
    requireField(warnings, "date", entry.issued);
    requireField(warnings, "publisher", entry.publisher);
    return warnings;
  }

  return warnings;
}

function displayTitle(entry: EditableEntry): string | undefined {
  return entry.title?.trim() || undefined;
}

/**
 * Build an immutable import-review list. Exact source identities are surfaced as
 * candidates and initially excluded, but users can deliberately include them. A final
 * duplicate check still happens in addCitations() when the review is committed.
 */
export function stageBibliographyImport(
  entries: readonly EditableEntry[],
  existing: readonly SavedCitation[]
): StagedImportEntry[] {
  const existingByKey = new Map<string, SavedCitation>();
  for (const citation of existing) {
    const key = entryIdentityKey(citation.entry);
    if (key && !existingByKey.has(key)) {
      existingByKey.set(key, citation);
    }
  }

  const firstBatchByKey = new Map<string, EditableEntry>();
  return entries.map((entry, index) => {
    const key = entryIdentityKey(entry);
    const existingMatch = key ? existingByKey.get(key) : undefined;
    const batchMatch = key ? firstBatchByKey.get(key) : undefined;
    let duplicate: ImportDuplicateCandidate | undefined;
    if (existingMatch) {
      duplicate = {
        kind: "existing",
        title: displayTitle(existingMatch.entry),
      };
    } else if (batchMatch) {
      duplicate = { kind: "batch", title: displayTitle(batchMatch) };
    }
    if (key && !firstBatchByKey.has(key)) {
      firstBatchByKey.set(key, entry);
    }

    return {
      id: `import-${index}`,
      entry,
      included: duplicate === undefined,
      ...(duplicate ? { duplicate } : {}),
      warnings: metadataWarnings(entry),
    };
  });
}

export function summarizeImportOutcome(
  staged: readonly StagedImportEntry[],
  formatted: readonly (string | null)[],
  added: number,
  duplicates: number,
  overLimit: number
): ImportOutcome {
  const included = staged.filter((item) => item.included);
  const failed = included.reduce(
    (count, _item, index) => count + (formatted[index] ? 0 : 1),
    0
  );
  return {
    added,
    duplicates,
    excluded: staged.length - included.length,
    failed,
    overLimit,
  };
}
